// Edge Function base URL. Supabase Functions are served at
// `${SUPABASE_URL}/functions/v1/<name>`. We send anon auth header to pass
// apikey verification (functions with verify_jwt=false).

import { supabase } from './supabase';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';

interface EdgeError {
  ok: false;
  error: string;
}

interface EdgeOk<T> {
  ok: true;
  data?: T;
  [key: string]: unknown;
}

type EdgeResult<T> = EdgeOk<T> | EdgeError;

async function call<T>(name: string, body: Record<string, unknown>): Promise<EdgeResult<T>> {
  const url = `${SUPABASE_URL}/functions/v1/${name}`;
  // Pull the anon key from the browser Supabase client.
  const anonKey = (supabase as unknown as { supabaseKey: string }).supabaseKey ?? '';
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as EdgeResult<T>;
    if (!res.ok) {
      return { ok: false, error: (data as EdgeError).error || `Edge Function error (${res.status})` };
    }
    return data;
  } catch (err) {
    return { ok: false, error: (err as Error).message || 'Network error' };
  }
}

export interface ContactInput {
  name: string;
  email: string;
  company?: string;
  topic: string;
  budget?: string;
  /** Selected when topic is "Get videos". Values: "1".."9" | "video series". */
  number_of_videos?: string;
  message: string;
  consent: boolean;
}

export async function submitContact(input: ContactInput) {
  return call<{ id: string }>('contact', { ...input, hp: '' });
}

export interface ApplicationInput {
  role_slug?: string;
  full_name: string;
  email: string;
  phone?: string;
  linkedin_url?: string;
  portfolio_url?: string;
  cover_letter?: string;
  cv_path?: string;
  consent: boolean;
}

export async function submitApplication(input: ApplicationInput) {
  return call<{ id: string }>('applications', { ...input, hp: '' });
}

export async function uploadCv(file: File): Promise<{ path: string } | { error: string }> {
  // The `cvs` storage bucket only accepts INSERTs from authenticated admin/hr
  // roles. Anonymous applicants upload through the `upload-cv` edge function,
  // which validates the file and writes via the service-role key.
  const url = `${SUPABASE_URL}/functions/v1/upload-cv`;
  const anonKey = (supabase as unknown as { supabaseKey: string }).supabaseKey ?? '';
  try {
    const form = new FormData();
    form.append('file', file, file.name);
    const res = await fetch(url, {
      method: 'POST',
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
      body: form,
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; path?: string; error?: string };
    if (!res.ok || data.error || !data.path) {
      return { error: data.error ?? `Upload failed (${res.status}).` };
    }
    return { path: data.path };
  } catch (err) {
    return { error: (err as Error).message || 'Network error' };
  }
}